import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  ChevronDown,
  ChevronUp,
  FlaskConical,
  Play,
  Save,
  Settings as SettingsIcon,
  CheckCircle,
  AlertCircle,
  XCircle,
} from 'lucide-react';

import { ErrorToast } from 'components';
import externalIntegrationApi from 'api/external-integration';
import VesselsTable from './VesselsTable';
import RunHistory from './RunHistory';
import MappingEditor from './MappingEditor';
import TestSampleModal from './TestSampleModal';
import { CRON_PRESETS, DEFAULT_CONFIG } from './constants';

// Top-level layout for the External member sync feature. The ops users only
// care about (a) which vessels are being synced and (b) what happened on the
// last few runs. The technical wiring (URL, mapping, defaults, slack) is
// tucked behind a "Configuration" collapsible.

const STATUS_POLL_MS = 10_000;

const ExternalSyncCard = ({ clientId, userEmail }) => {
  const [expanded, setExpanded] = useState(false);
  const [config, setConfig] = useState(null);
  const [extraBodyJson, setExtraBodyJson] = useState('{}');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [dirty, setDirty] = useState(false);

  const loadConfig = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const [cfgResp, statusResp] = await Promise.all([
        externalIntegrationApi.getByClientId(clientId),
        externalIntegrationApi.getStatus(clientId),
      ]);
      const cfg = cfgResp?.data?.data ?? { ...DEFAULT_CONFIG, clientId };
      setConfig(cfg);
      setExtraBodyJson(JSON.stringify(cfg.source?.extraBody ?? {}, null, 2));
      setStatus(statusResp?.data?.data ?? null);
      // First-time configs (no URL set) auto-open the advanced panel so the
      // admin can fill it in. Otherwise keep it tucked.
      setAdvancedOpen(!cfg.source?.url);
      setDirty(false);
    } catch (err) {
      toast.error(<ErrorToast error={err} />);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (expanded) loadConfig();
  }, [expanded, loadConfig]);

  useEffect(() => {
    if (!expanded || !clientId) return undefined;
    const tick = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const r = await externalIntegrationApi.getStatus(clientId);
        setStatus(r?.data?.data ?? null);
      } catch {
        /* swallow poll errors */
      }
    };
    const handle = setInterval(tick, STATUS_POLL_MS);
    return () => clearInterval(handle);
  }, [expanded, clientId]);

  const mutate = (next) => {
    setConfig(next);
    setDirty(true);
  };
  const patchSource = (patch) => mutate({ ...config, source: { ...config.source, ...patch } });
  const patchAuth = (patch) =>
    mutate({ ...config, source: { ...config.source, auth: { ...config.source.auth, ...patch } } });
  const patchDefaults = (patch) => mutate({ ...config, defaults: { ...config.defaults, ...patch } });
  const patchClinic = (patch) =>
    mutate({ ...config, defaults: { ...config.defaults, clinic: { ...config.defaults.clinic, ...patch } } });
  const patchNotifications = (patch) =>
    mutate({ ...config, notifications: { ...config.notifications, ...patch } });

  const handleSave = async () => {
    let extraBody;
    try {
      extraBody = JSON.parse(extraBodyJson);
    } catch {
      toast.error('Extra body JSON is invalid');
      return;
    }
    setSaving(true);
    try {
      await externalIntegrationApi.upsert({
        ...config,
        clientId,
        source: { ...config.source, extraBody },
        updatedBy: userEmail,
      });
      toast.success('Saved');
      setDirty(false);
    } catch (err) {
      toast.error(<ErrorToast error={err} />);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTestOpen(true);
    setTestLoading(true);
    setTestResult(null);
    setTestError(null);
    try {
      const r = await externalIntegrationApi.test(clientId);
      setTestResult(r?.data?.data ?? null);
    } catch (err) {
      setTestError(err?.response?.data?.message ?? err?.message ?? 'unknown error');
    } finally {
      setTestLoading(false);
    }
  };

  const refreshStatus = () =>
    externalIntegrationApi
      .getStatus(clientId)
      .then((r) => setStatus(r?.data?.data ?? null))
      .catch(() => {});

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      await externalIntegrationApi.syncNow(clientId);
      toast.success('Sync started for all vessels');
      setTimeout(refreshStatus, 3000);
    } catch (err) {
      toast.error(<ErrorToast error={err} />);
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncOne = async (value) => {
    try {
      await externalIntegrationApi.syncOneGroup(clientId, value);
      toast.success(`Sync started for ${value}`);
      setTimeout(refreshStatus, 2000);
    } catch (err) {
      toast.error(<ErrorToast error={err} />);
    }
  };

  return (
    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-5">
      <CardHeader
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
        config={config}
        status={status}
      />

      {expanded && (
        <div className="tw-mt-5 tw-space-y-5">
          {loading || !config ? (
            <div className="tw-text-sm tw-text-gray-500">Loading…</div>
          ) : (
            <>
              <TopBar
                enabled={Boolean(config.enabled)}
                onEnabledChange={(v) => mutate({ ...config, enabled: v })}
                cron={config.schedule?.cron ?? null}
                onCronChange={(v) => mutate({ ...config, schedule: { cron: v === '' ? null : v } })}
                syncing={syncing}
                onSyncAll={handleSyncAll}
                canSync={Boolean(config.enabled) && !dirty && (config.groups ?? []).length > 0}
                dirty={dirty}
              />

              <Section
                title="Vessels"
                hint="Each vessel is fetched separately. Use the per-row Sync button to refresh just one."
              >
                <VesselsTable
                  vessels={config.groups ?? []}
                  onChange={(groups) => mutate({ ...config, groups })}
                  onSyncOne={handleSyncOne}
                  savedDisabled={dirty || !config.enabled}
                />
              </Section>

              <Section title="Recent activity">
                <RunHistory status={status} />
              </Section>

              <Advanced
                open={advancedOpen}
                onToggle={() => setAdvancedOpen((v) => !v)}
                config={config}
                extraBodyJson={extraBodyJson}
                onMapping={(mapping) => mutate({ ...config, mapping })}
                onExtraBodyJson={(v) => {
                  setExtraBodyJson(v);
                  setDirty(true);
                }}
                patchSource={patchSource}
                patchAuth={patchAuth}
                patchDefaults={patchDefaults}
                patchClinic={patchClinic}
                patchNotifications={patchNotifications}
                onTest={handleTest}
              />

              <Footer dirty={dirty} saving={saving} onSave={handleSave} onDiscard={loadConfig} />
            </>
          )}
        </div>
      )}

      <TestSampleModal
        open={testOpen}
        onClose={() => setTestOpen(false)}
        loading={testLoading}
        result={testResult}
        error={testError}
      />
    </div>
  );
};

// ---------- card-level subcomponents ----------

const CardHeader = ({ expanded, onToggle, config, status }) => {
  const lastRun = status?.lastRunAt ? new Date(status.lastRunAt) : null;
  const enabled = config?.enabled;
  const vessels = config?.groups?.length ?? 0;
  return (
    <button
      type="button"
      onClick={onToggle}
      className="tw-w-full tw-flex tw-items-center tw-justify-between tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer"
    >
      <div className="tw-text-left">
        <p className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-m-0">External member sync</p>
        <div className="tw-text-xs tw-text-gray-400 tw-mt-0.5 tw-flex tw-items-center tw-gap-2">
          {enabled ? (
            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-green-600">
              <CheckCircle className="tw-w-3 tw-h-3" /> Active
            </span>
          ) : (
            <span className="tw-text-gray-400">Off</span>
          )}
          <span>·</span>
          <span>
            {vessels} vessel{plural(vessels)}
          </span>
          {lastRun && (
            <>
              <span>·</span>
              <span>Last run {lastRun.toLocaleString()}</span>
              {status?.lastRunStatus === 'error' && <XCircle className="tw-w-3 tw-h-3 tw-text-red-600" />}
              {status?.lastRunStatus === 'partial' && (
                <AlertCircle className="tw-w-3 tw-h-3 tw-text-amber-500" />
              )}
            </>
          )}
        </div>
      </div>
      {expanded ? (
        <ChevronUp className="tw-w-5 tw-h-5 tw-text-gray-500" />
      ) : (
        <ChevronDown className="tw-w-5 tw-h-5 tw-text-gray-500" />
      )}
    </button>
  );
};

const TopBar = ({ enabled, onEnabledChange, cron, onCronChange, syncing, onSyncAll, canSync, dirty }) => (
  <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3 tw-p-4 tw-bg-gray-50 tw-rounded-lg">
    <div className="tw-flex tw-items-center tw-gap-4 tw-flex-wrap">
      <label className="tw-inline-flex tw-items-center tw-gap-2 tw-cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onEnabledChange(e.target.checked)}
          className="tw-w-4 tw-h-4"
        />
        <span className="tw-text-sm tw-font-medium tw-text-gray-800">Sync enabled</span>
      </label>
      <div className="tw-flex tw-items-center tw-gap-2">
        <span className="tw-text-xs tw-uppercase tw-text-gray-400 tw-font-semibold">Schedule</span>
        <select
          value={cron ?? ''}
          onChange={(e) => onCronChange(e.target.value)}
          className="tw-px-3 tw-py-1.5 tw-text-sm tw-border tw-border-gray-200 tw-rounded-lg tw-bg-white"
        >
          {CRON_PRESETS.map((p) => (
            <option key={p.value ?? 'off'} value={p.value ?? ''}>
              {p.label}
            </option>
          ))}
        </select>
      </div>
    </div>
    <button
      type="button"
      onClick={onSyncAll}
      disabled={!canSync || syncing}
      title={
        dirty
          ? 'Save changes first'
          : !enabled
          ? 'Enable sync first'
          : 'Run sync now for every vessel'
      }
      className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-bg-indigo-600 tw-rounded-lg tw-border-0 tw-cursor-pointer disabled:tw-opacity-50 disabled:tw-cursor-not-allowed hover:tw-bg-indigo-700"
    >
      <Play className="tw-w-4 tw-h-4" />
      {syncing ? 'Starting…' : 'Sync now'}
    </button>
  </div>
);

const Footer = ({ dirty, saving, onSave, onDiscard }) => {
  if (!dirty) {
    return <p className="tw-text-xs tw-text-gray-400 tw-text-right tw-m-0">All changes saved.</p>;
  }
  return (
    <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-pt-3 tw-border-t tw-border-gray-100">
      <span className="tw-text-xs tw-text-amber-600 tw-flex-1">You have unsaved changes.</span>
      <button
        type="button"
        onClick={onDiscard}
        className="tw-px-3 tw-py-2 tw-text-sm tw-text-gray-600 tw-bg-transparent tw-border-0 tw-cursor-pointer hover:tw-text-gray-900"
      >
        Discard
      </button>
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-gray-800 tw-rounded-lg tw-border-0 tw-cursor-pointer disabled:tw-opacity-60 hover:tw-bg-gray-900"
      >
        <Save className="tw-w-4 tw-h-4" />
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
};

// ---------- advanced (technical) panel ----------

const Advanced = ({
  open,
  onToggle,
  config,
  extraBodyJson,
  onMapping,
  onExtraBodyJson,
  patchSource,
  patchAuth,
  patchDefaults,
  patchClinic,
  patchNotifications,
  onTest,
}) => (
  <div className="tw-border tw-border-gray-200 tw-rounded-lg">
    <button
      type="button"
      onClick={onToggle}
      className="tw-w-full tw-flex tw-items-center tw-justify-between tw-bg-transparent tw-border-0 tw-p-4 tw-cursor-pointer"
    >
      <div className="tw-flex tw-items-center tw-gap-2 tw-text-left">
        <SettingsIcon className="tw-w-4 tw-h-4 tw-text-gray-500" />
        <div>
          <p className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-m-0">Configuration</p>
          <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5 tw-mb-0">
            REST API endpoint, auth, field mapping, clinic, notifications.
          </p>
        </div>
      </div>
      {open ? (
        <ChevronUp className="tw-w-5 tw-h-5 tw-text-gray-500" />
      ) : (
        <ChevronDown className="tw-w-5 tw-h-5 tw-text-gray-500" />
      )}
    </button>
    {open && (
      <div className="tw-px-4 tw-pb-4 tw-space-y-5 tw-border-t tw-border-gray-100">
        <Section title="Source">
          <Grid>
            <Field label="URL">
              <Input value={config.source.url} onChange={(v) => patchSource({ url: v })} />
            </Field>
            <Field label="Method">
              <Select
                value={config.source.method}
                onChange={(v) => patchSource({ method: v })}
                options={['GET', 'POST']}
              />
            </Field>
            <Field label="Auth header name">
              <Input value={config.source.auth.headerName} onChange={(v) => patchAuth({ headerName: v })} />
            </Field>
            <Field label="Secret env var name" hint="stark reads process.env[name] at runtime">
              <Input
                value={config.source.auth.secretEnvName}
                onChange={(v) => patchAuth({ secretEnvName: v.toUpperCase() })}
                placeholder="ONECARE_CREW_API_KEY"
              />
            </Field>
            <Field label="Filter param name">
              <Input value={config.source.filterParamName} onChange={(v) => patchSource({ filterParamName: v })} />
            </Field>
            <Field label="Response path">
              <Input value={config.source.responsePath} onChange={(v) => patchSource({ responsePath: v })} />
            </Field>
          </Grid>
          <Field label="Extra body (JSON)">
            <TextArea value={extraBodyJson} onChange={onExtraBodyJson} rows={3} mono />
          </Field>
        </Section>

        <Section
          title="Field mapping"
          hint="Map each Bedrock member field to a field in the source response. Add transforms (date format, prefix, true/false, etc.) as needed."
        >
          <MappingEditor value={config.mapping ?? {}} onChange={onMapping} />
        </Section>

        <Section title="Defaults">
          <Grid>
            <Field label="Employer name">
              <Input value={config.defaults.employerName} onChange={(v) => patchDefaults({ employerName: v })} />
            </Field>
            <Field label="Email prefix (optional)">
              <Input value={config.defaults.emailPrefix} onChange={(v) => patchDefaults({ emailPrefix: v })} />
            </Field>
            <Field label="ID type">
              <Input value={config.defaults.idType} onChange={(v) => patchDefaults({ idType: v })} />
            </Field>
            <Field label="Clinic id">
              <Input value={config.defaults.clinic.id} onChange={(v) => patchClinic({ id: v })} />
            </Field>
            <Field label="Clinic clientId">
              <Input value={config.defaults.clinic.clientId} onChange={(v) => patchClinic({ clientId: v })} />
            </Field>
            <Field label="Clinic name">
              <Input value={config.defaults.clinic.name} onChange={(v) => patchClinic({ name: v })} />
            </Field>
          </Grid>
        </Section>

        <Section title="Slack notifications">
          <Grid>
            <Field label="Channel">
              <Input
                value={config.notifications.slackChannel}
                onChange={(v) => patchNotifications({ slackChannel: v })}
                placeholder="#dev-riverr-noti"
              />
            </Field>
            <Field label="Batch size">
              <Input
                type="number"
                value={String(config.notifications.slackBatchSize ?? 5)}
                onChange={(v) => patchNotifications({ slackBatchSize: Number(v) || 5 })}
              />
            </Field>
          </Grid>
        </Section>

        <div className="tw-flex tw-justify-end">
          <button
            type="button"
            onClick={onTest}
            className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-800 tw-bg-gray-100 tw-rounded-lg tw-border-0 tw-cursor-pointer hover:tw-bg-gray-200"
          >
            <FlaskConical className="tw-w-4 tw-h-4" />
            Test sample
          </button>
        </div>
      </div>
    )}
  </div>
);

// ---------- tiny primitives (kept local) ----------

const Section = ({ title, hint, children }) => (
  <div>
    <p className="tw-text-xs tw-uppercase tw-tracking-wider tw-text-gray-400 tw-font-semibold tw-mb-2">{title}</p>
    {hint && <p className="tw-text-xs tw-text-gray-400 tw-mt-0 tw-mb-2">{hint}</p>}
    {children}
  </div>
);

const Grid = ({ children }) => (
  <div className="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-3 tw-mb-3">{children}</div>
);

const Field = ({ label, hint, children }) => (
  <div>
    <p className="tw-text-xs tw-text-gray-500 tw-font-medium tw-mb-1">{label}</p>
    {children}
    {hint && <p className="tw-text-[11px] tw-text-gray-400 tw-mt-1 tw-mb-0">{hint}</p>}
  </div>
);

const Input = ({ value, onChange, placeholder, type = 'text' }) => (
  <input
    type={type}
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-gray-200 tw-rounded-lg focus:tw-border-indigo-500 focus:tw-outline-none"
  />
);

const TextArea = ({ value, onChange, rows = 4, mono }) => (
  <textarea
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    rows={rows}
    className={[
      'tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-gray-200 tw-rounded-lg focus:tw-border-indigo-500 focus:tw-outline-none',
      mono ? 'tw-font-mono tw-text-xs' : '',
    ].join(' ')}
  />
);

const Select = ({ value, onChange, options }) => (
  <select
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    className="tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-gray-200 tw-rounded-lg tw-bg-white"
  >
    {options.map((o) => (
      <option key={o} value={o}>
        {o}
      </option>
    ))}
  </select>
);

const plural = (n) => (n === 1 ? '' : 's');

export default ExternalSyncCard;
